import React, { Fragment, useEffect, useState } from "react";
import { Row } from "reactstrap";
import {
  CircularProgress,
  Collapse,
  DialogContentText,
  IconButton,
  Stack,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import PropTypes from "prop-types";

import ViewUtilityConsumerMeterField from "./ViewUtilityConsumerMeterField";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useLazyGetMeterDetailsByMeterId } from "apis/UtilityConsumerMeter";
import { useGetMeterAssignByUtilityConsumerId } from "apis/UtilityConsumer/utilityConsumer";

const viewFields = [
  { heading: "Meter" },

  { label: "Sr No.", key: "meter.srNo", isRequired: true },
  { label: "Class", key: "meter.class", isRequired: true },
  {
    label: "Type",
    key: "meter.type",
    enumName: "meterTypes",
    isRequired: true,
  },
  { label: "lab No.", key: "meter.labNo" },
  {
    label: "Phase Or Wire",
    key: "meter.phaseOrWire",
    enumName: "loadOptions",
    isRequired: true,
  },
  { label: "Voltage Rating", key: "meter.voltageRating", isRequired: true },
  { label: "Current Rating", key: "meter.currentRating", isRequired: true },
  { label: "Meter CTR", key: "meter.meterCTR", isRequired: true },
  { label: "Meter PTR", key: "meter.meterPTR" },
  { label: "Pulse", key: "meter.pulseOrkwH", unit: "kwH", isRequired: true },
  { label: "Dial MF", key: "meter.dialMF", isRequired: true },

  { heading: "seal" },

  {
    label: "Meter Body Seal Left Bottom",
    key: "seal.meterBodySealLeftBottom",
  },
  { label: "Meter Body Seal Left Top", key: "seal.meterBodySealLeftTop" },
  {
    label: "Meter Body Seal Right Top",
    key: "seal.meterBodySealRightTop",
  },
  {
    label: "Meter Body Seal Right Bottom",
    key: "seal.meterBodySealRightBottom",
  },
  {
    label: "Meter Terminal Cover Seal Left",
    key: "seal.meterTerminalCoverSealLeft",
  },
  {
    label: "Meter Terminal Cover Seal Right",
    key: "seal.meterTerminalCoverSealRight",
  },
  { label: "Meter Reset Button Seal", key: "seal.meterResetButtonSeal" },
  { label: "Meter Box Or Panel Seal Top", key: "seal.meterBoxOrPanelSealTop" },
  {
    label: "Meter Box Or Panel Seal Middle",
    key: "seal.meterBoxOrPanelSealMiddle",
  },
  {
    label: "Meter Box Or Panel Seal Bottom",
    key: "seal.meterBoxOrPanelSealBottom",
  },
  {
    label: "Test Terminal Block Seal Left",
    key: "seal.testTerminalBlockSealLeft",
  },
  {
    label: "Test Terminal Block Seal Right",
    key: "seal.testTerminalBlockSealRight",
  },
  {
    label: "Line CT Seal R Phase",
    key: "seal.lineCTSealRPhase",
  },
  {
    label: "Line CT Seal Y Phase",
    key: "seal.lineCTSealYPhase",
  },
  {
    label: "Line CT Seal B Phase",
    key: "seal.lineCTSealBPhase",
  },
  {
    label: "PT Change Over Box Seal Top",
    key: "seal.ptChangeOverBoxSealTop",
  },
  {
    label: "PT Change Over Box Seal Bottom",
    key: "seal.ptChangeOverBoxSealBottom",
  },

  { heading: "Meter Display Detail" },

  { label: "RN", key: "accucheckTest.meterDisplayDetail.rn" },
  { label: "YN", key: "accucheckTest.meterDisplayDetail.yn" },
  {
    label: "BN",
    key: "accucheckTest.meterDisplayDetail.bn",
  },
  { label: "KW", key: "accucheckTest.meterDisplayDetail.kw" },
  {
    label: "IR",
    key: "accucheckTest.meterDisplayDetail.ir",
  },
  {
    label: "IY",
    key: "accucheckTest.meterDisplayDetail.iy",
  },
  {
    label: "IB",
    key: "accucheckTest.meterDisplayDetail.ib",
  },
  {
    label: "PF",
    key: "accucheckTest.meterDisplayDetail.pf",
  },

  { heading: "Accucheck Details" },

  { label: "RN", key: "accucheckTest.accucheckDetail.rn" },
  { label: "YN", key: "accucheckTest.accucheckDetail.yn" },
  {
    label: "BN",
    key: "accucheckTest.accucheckDetail.bn",
  },
  {
    label: "IR",
    key: "accucheckTest.accucheckDetail.ir",
  },
  {
    label: "IY",
    key: "accucheckTest.accucheckDetail.iy",
  },
  {
    label: "IB",
    key: "accucheckTest.accucheckDetail.ib",
  },
  {
    label: "PF",
    key: "accucheckTest.accucheckDetail.pf",
  },
  {
    label: "Hz",
    key: "accucheckTest.accucheckDetail.hz",
  },
  {
    label: "Phase Sequence",
    key: "accucheckTest.accucheckDetail.phaseSequence",
  },
  { label: "P", key: "accucheckTest.accucheckDetail.p_KW", unit: "KW" },
  { label: "Q", key: "accucheckTest.accucheckDetail.p_KW", unit: "KVAR" },
  { label: "S", key: "accucheckTest.accucheckDetail.p_KW", unit: "KVA" },
  { label: "F", key: "accucheckTest.accucheckDetail.p_KW", unit: "kwh" },

  { heading: "Meter Reading Test" },
  {
    label: "Initial",
    key: "accucheckTest.meterReadingTest.initial",
    unit: "KWH",
  },
  {
    label: "Final",
    key: "accucheckTest.meterReadingTest.final",
    unit: "KWH",
  },

  {
    label: "Difference",
    key: "accucheckTest.meterReadingTest.difference",
    unit: "KWH",
  },
  {
    label: "Error",
    key: "accucheckTest.meterReadingTest.error",
    unit: "%",
  },

  { heading: "Service Connection" },

  {
    label: "Line CT Ratio",
    key: "serviceConnection.lineCTRatio",
    isRequired: true,
  },
  {
    label: "Line PT Ratio",
    key: "serviceConnection.linePTRatio",
    isRequired: true,
  },
  {
    label: "R Phase",
    key: "serviceConnection.rPhase",
    isRequired: true,
  },
  {
    label: "Y Phase",
    key: "serviceConnection.yPhase",
    isRequired: true,
  },
  {
    label: "B Phase",
    key: "serviceConnection.bPhase",
    isRequired: true,
  },
  {
    label: "Final MF",
    key: "serviceConnection.finalMF",
    isRequired: true,
  },
];

export const ViewUtilityConsumerMeterInfo = ({
  utilityConsumerId,
  handleClose,
}) => {
  const {
    data: { data: consumerMeters } = {},
    isLoading: isConsumerMetersLoading,
  } = useGetMeterAssignByUtilityConsumerId(utilityConsumerId);

  const [
    getMeterDataByMeterId,
    {
      data: { data: meterData } = {},
      isLoading: isMeterDataLoading,
      isFetching: isMeterDataFetching,
    },
  ] = useLazyGetMeterDetailsByMeterId();

  const [consumerMeterId, setConsumerMeterId] = useState("");

  const handleToggle = (id) => {
    if (consumerMeterId === id) {
      setConsumerMeterId("");
    } else {
      setConsumerMeterId(id);
    }
  };

  useEffect(() => {
    if (consumerMeterId) {
      (async () => {
        try {
          await getMeterDataByMeterId(consumerMeterId);
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [consumerMeterId]);

  useEffect(() => {
    if (consumerMeters?.length === 1) {
      setConsumerMeterId(consumerMeters[0].meterId);
    }
  }, [consumerMeters]);

  if (isConsumerMetersLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Meter Information"
      maxWidth="lg"
    >
      {consumerMeters?.map(({ meterId, serialNumber, timeStamp }) => (
        <Fragment key={meterId}>
          <div
            className="fs-4 fw-semibold mb-3 border-bottom"
            onClick={() => handleToggle(meterId)}
            key={meterId}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className="cursor-pointer"
            >
              <div className="d-flex align-items-center gap-1">
                <p className="fw-bold mb-0">{`Serial Number: ${serialNumber}`}</p>
                <p className="fw-light fs-5 mb-0">{`(${convertUTCDateToLocalDate(
                  timeStamp
                ).format("DD-MM-YYYY hh:mm:ss A")})`}</p>
              </div>
              <IconButton>
                {consumerMeterId === meterId ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>
          </div>
          <DialogContentText variant="subtitle2" component="div">
            <Collapse
              in={consumerMeterId === meterId}
              timeout="auto"
              unmountOnExit
            >
              {isMeterDataLoading || isMeterDataFetching ? (
                <div className="d-flex justify-content-center">
                  <CircularProgress size={30} />
                </div>
              ) : (
                <Row>
                  {meterData &&
                    viewFields.map((item, index) => (
                      <ViewUtilityConsumerMeterField
                        item={item}
                        meterData={meterData}
                        key={index}
                      />
                    ))}
                </Row>
              )}
            </Collapse>
          </DialogContentText>
        </Fragment>
      ))}
    </Modal>
  );
};

ViewUtilityConsumerMeterInfo.propTypes = {
  utilityConsumerId: PropTypes.number,
  handleClose: PropTypes.func,
};
